import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  zoomLevelChanged,
  zoomPositionChanged,
  zoomReset,
  selectZoomState,
} from 'features/content-zoom/contentZoomSlice';
import { useZoomControlVisibility } from 'features/content-zoom/hooks/useZoomControlVisibility';
import { SCREENSHARE_ZOOM_LEVELS, CONTENT_ZOOM_LEVELS } from 'features/content-zoom/constants';

export const useZoomControl = () => {
  const { zoomId, visible, isScreenshare } = useZoomControlVisibility();

  const zoomLevels = isScreenshare ? SCREENSHARE_ZOOM_LEVELS : CONTENT_ZOOM_LEVELS;

  const dispatch = useAppDispatch();
  const zoomState = useAppSelector((state) => selectZoomState(state, zoomId));

  const scale = zoomState?.scale ?? 1;
  const currentIndex = zoomLevels.indexOf(scale);

  const handleZoomIn = () => {
    const nextIndex = Math.min(currentIndex + 1, zoomLevels.length - 1);
    dispatch(zoomLevelChanged({ zoomId, scale: zoomLevels[nextIndex] }));
  };

  const handleZoomOut = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    dispatch(zoomLevelChanged({ zoomId, scale: zoomLevels[prevIndex] }));
  };

  const handleReset = () => {
    dispatch(zoomReset({ zoomId }));
  };

  const zoomToLevel = (targetScale: number) => {
    dispatch(
      zoomLevelChanged({
        zoomId,
        scale: targetScale,
        resetPosition: targetScale === 1,
      })
    );
  };

  const panToPosition = (offsetX: number, offsetY: number) => {
    dispatch(zoomPositionChanged({ zoomId, offsetX, offsetY }));
  };

  return {
    zoomId,
    visible,
    scale,
    currentIndex,
    zoomLevels,
    handleZoomIn,
    handleZoomOut,
    handleReset,
    zoomToLevel,
    panToPosition,
    disabled: {
      zoomOut: currentIndex === 0,
      zoomIn: currentIndex === zoomLevels.length - 1,
    },
  };
};
